import { Button, Select } from '@curri/components'
import { useState, type ReactNode } from 'react'

import type { AccountKeys } from '../../api/keys.ts'
import { Panel, PANEL_HAIRLINE } from '../webhooks/Panel.tsx'
import { Pill } from '../webhooks/Pill.tsx'

import { CurlConsole } from './CurlConsole.tsx'
import { CopyButton, SECRET_MASK } from './Secret.tsx'

// How a partner actually authenticates, split the way the question always
// arrives: what the header *is* (left), and what it looks like on the wire
// (right).
//
// The dropdown picks which key the example is built from. Sandbox leads because
// it is the credential someone setting up should be pasting into a first
// request, and the one that costs least if it ends up in a screenshot — but
// production is a real option rather than a footnote, because someone debugging
// a live 401 needs to compare against the exact string they are sending, not a
// sandbox stand-in of it.
//
// One reveal state drives both columns. It is one credential shown twice, and
// two toggles that disagree about whether it is on screen is worse than either
// answer on its own.

type Mode = 'sandbox' | 'production'

const MODES: readonly Mode[] = ['sandbox', 'production']

const MODE_LABELS: Record<Mode, string> = {
  sandbox: 'Sandbox',
  production: 'Production',
}

const keyFor = (keys: AccountKeys, mode: Mode): string | null =>
  mode === 'sandbox' ? keys.sandboxKey : keys.apiKey

/** The modes this account can actually build an example for, sandbox first. */
const availableModes = (keys: AccountKeys): Mode[] =>
  MODES.filter(mode => keyFor(keys, mode) !== null)

/**
 * The dropdown hands back a bare string. Narrowing it here rather than casting
 * keeps `Mode` meaning what it says, even though the options are ours.
 */
const asMode = (value: string): Mode | null =>
  MODES.find(mode => mode === value) ?? null

const FieldLabel = ({ children }: { children: ReactNode }) => (
  <span
    style={{
      display: 'block',
      marginBottom: 6,
      fontSize: 11,
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.08em',
      color: 'var(--curri-text-tertiary)',
    }}
  >
    {children}
  </span>
)

/**
 * Which key the example uses. A dropdown only when there is a real choice: a
 * one-option select advertises a decision the account cannot make.
 */
const KeySelector = ({
  mode,
  modes,
  onChange,
}: {
  mode: Mode
  modes: Mode[]
  onChange: (next: Mode) => void
}) => {
  if (modes.length < 2) {
    return (
      <div>
        <FieldLabel>Key in this example</FieldLabel>
        <Pill tone={mode === 'production' ? 'info' : 'neutral'}>
          {MODE_LABELS[mode]}
        </Pill>
      </div>
    )
  }

  return (
    <Select
      label="Key in this example"
      onValueChange={value => {
        const next = asMode(value)
        if (next) onChange(next)
      }}
      options={modes.map(candidate => ({
        label: MODE_LABELS[candidate],
        value: candidate,
      }))}
      size="base"
      value={mode}
    />
  )
}

/** The assembled credential itself — the thing most people came here to copy. */
const TokenField = ({
  encoded,
  onToggleReveal,
  revealed,
}: {
  encoded: string
  onToggleReveal: () => void
  revealed: boolean
}) => (
  <div>
    <FieldLabel>Basic token</FieldLabel>
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 10,
        padding: '10px 12px',
        background: 'var(--curri-canvas)',
        border: `1px solid ${PANEL_HAIRLINE}`,
        borderRadius: 'var(--curri-radius-md)',
      }}
    >
      <code
        style={{
          flex: '1 1 150px',
          minWidth: 0,
          fontFamily: 'var(--curri-font-mono)',
          fontSize: 12.5,
          lineHeight: 1.5,
          color: revealed ? 'var(--curri-text)' : 'var(--curri-text-tertiary)',
          overflowWrap: 'anywhere',
          ...(revealed ? {} : { letterSpacing: '0.08em' }),
        }}
      >
        {revealed ? encoded : SECRET_MASK}
      </code>
      <Button
        label={revealed ? 'Hide' : 'Reveal'}
        onClick={onToggleReveal}
        size="small"
        type="button"
        variant="tertiary"
      />
      {/* Copies the real token even while it is masked — the mask is about what
          a shared screen shows, not about what the clipboard may hold. */}
      <CopyButton value={encoded} />
    </div>
  </div>
)

const Explanation = ({
  externalId,
  mode,
}: {
  externalId: string
  mode: Mode
}) => (
  <>
    <p
      style={{
        margin: 0,
        fontSize: 13.5,
        lineHeight: 1.6,
        color: 'var(--curri-text-secondary)',
        textWrap: 'pretty',
      }}
    >
      Every request carries an <code>Authorization</code> header: the word{' '}
      <code>Basic</code>, then your external ID and an API key joined by a
      colon and base64-encoded. Nothing else — there is no login call and no
      token to refresh.
    </p>
    <code
      style={{
        display: 'block',
        fontFamily: 'var(--curri-font-mono)',
        fontSize: 12.5,
        lineHeight: 1.6,
        color: 'var(--curri-text-tertiary)',
        overflowWrap: 'anywhere',
      }}
    >
      base64({externalId}:&lt;{mode} key&gt;)
    </code>
  </>
)

const NoExample = () => (
  <p
    style={{
      margin: '14px 0 0',
      fontSize: 13,
      color: 'var(--curri-text-tertiary)',
    }}
  >
    No example to show yet — it needs an API key issued to your account.
  </p>
)

export const BasicAuthCard = ({ keys }: { keys: AccountKeys }) => {
  const modes = availableModes(keys)
  const [selected, setSelected] = useState<Mode>(() => modes[0] ?? 'sandbox')
  const [revealed, setRevealed] = useState(false)

  // Derived, not trusted: the selection is state and the keys are props, so a
  // render with a different account's keys can pair the two. Falling back to
  // what this account does have keeps the card from reporting "no example" for
  // an account holding the other key.
  const mode = keyFor(keys, selected) === null ? (modes[0] ?? selected) : selected

  const { externalId } = keys.owner
  const key = keyFor(keys, mode)
  const encoded =
    externalId !== null && key !== null
      ? btoa(`${externalId}:${key}`)
      : null

  return (
    <Panel>
      <Panel.Title meta={encoded === null ? undefined : `${mode} key`}>
        Basic authentication
      </Panel.Title>

      {encoded === null || externalId === null ? (
        <NoExample />
      ) : (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'flex-start',
            gap: 24,
            marginTop: 16,
          }}
        >
          <div
            style={{
              flex: '1 1 260px',
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 14,
            }}
          >
            <Explanation externalId={externalId} mode={mode} />
            <KeySelector mode={mode} modes={modes} onChange={setSelected} />
            <TokenField
              encoded={encoded}
              onToggleReveal={() => setRevealed(current => !current)}
              revealed={revealed}
            />
          </div>

          <div style={{ flex: '1.15 1 330px', minWidth: 0 }}>
            <CurlConsole encoded={encoded} revealed={revealed} />
          </div>
        </div>
      )}
    </Panel>
  )
}
